import React, { useEffect, useState } from 'react';
import { Table, Card, Tooltip, Avatar } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import cnnaid from '../../../../../../../assets/icons/transactions/cnnaid.png';
import cnnaip from '../../../../../../../assets/icons/transactions/cnnaip.png';
import cnnafingerprint from '../../../../../../../assets/icons/transactions/cnnafingerprint.png';
import { ReactComponent as GlobePhoneIcon } from '../../../../../../../assets/icons/transactions/globephone.svg';
import {
  DesktopOutlined,
  MobileOutlined,
  TableOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { isEmpty } from 'lodash';
import SessionJourneyExpand from './SessionJourneyExpand';
import {
  PrimaryTableRowText,
  PrimaryTableRowTextStyle,
  TableColumnTitle,
} from '../../../../../../shared/globalStyling/styledText';
import { SESSIONS_TOOLTIP } from '../../../../../../shared/constants/tooltips';
import { secondsToTimeText } from '../../../../../Advertiser/utils/secondsToTimeText';

const SessionsTable = ({
  data = [],
  loading = false,
  total,
  history,
  location,
  match,
  fetchSessionDetails,
  expandedRowsData = {},
  setExpandedRowsData,
  sessionDetailsLoading,
}) => {
  const { params } = match;
  const { pageNumber } = params;
  const urlParams = new URLSearchParams(location.search);
  const method = urlParams.get('method') ? urlParams.get('method') : 'All';
  const pageViews = urlParams.get('pageViews') ? urlParams.get('pageViews') : 'All';
  const url = urlParams.get('url') ? urlParams.get('url') : '';

  const [current, setCurrent] = useState(pageNumber || 1);

  useEffect(() => {
    setCurrent(pageNumber || 1);
  }, [pageNumber]);

  const columns = [
    {
      title: <TableColumnTitle text={'Session ID'} />,
      dataIndex: 'sessionId',
      key: 'sessionId',
      width: 180,
      ellipsis: true,
      onCell: PrimaryTableRowTextStyle,
    },
    {
      title: <TableColumnTitle text={'Start Time'} />,
      dataIndex: 'start',
      key: 'start',
      width: 180,
      onCell: PrimaryTableRowTextStyle,
      render: (text, { start }) => {
        return moment(start).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: <TableColumnTitle text={'End Time'} />,
      dataIndex: 'end',
      key: 'end',
      width: 180,
      onCell: PrimaryTableRowTextStyle,
      render: (text, { end }) => {
        return moment(end).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: <TableColumnTitle text={'Duration'} />,
      dataIndex: 'durationInSeconds',
      key: 'durationInSeconds',
      width: 120,
      onCell: PrimaryTableRowTextStyle,
      render: (text, { durationInSeconds }) => {
        return secondsToTimeText(durationInSeconds);
      },
    },
    {
      title: <TableColumnTitle text={'Page Views'} />,
      dataIndex: 'pageViews',
      key: 'pageViews',
      width: 110,
      onCell: PrimaryTableRowTextStyle,
      render: (text, { pageViews }) => {
        return pageViews;
      },
    },
    {
      title: <TableColumnTitle text={'Landing Page'} />,
      dataIndex: 'landingPage',
      key: 'landingPage',
      width: 250,
      ellipsis: true,
      onCell: PrimaryTableRowTextStyle,
    },
    {
      title: <TableColumnTitle text={'Exit Page'} />,
      dataIndex: 'exitPage',
      key: 'exitPage',
      width: 250,
      ellipsis: true,
    },
    {
      title: <TableColumnTitle text={''} />,
      dataIndex: 'deviceType',
      key: 'deviceType',
      width: 80,
      render: (text, { deviceType }) => {
        const getIcon = () => {
          if (deviceType === 'Mobile') {
            return <MobileOutlined />;
          } else if (deviceType === 'Tablet') {
            return <TableOutlined />;
          }
          return <DesktopOutlined />;
        };
        return (
          <>
            {getIcon()}
            {deviceType}
          </>
        );
      },
    },
    {
      title: <TableColumnTitle text={''} />,
      dataIndex: 'attrMethod',
      key: 'attrMethod',
      align: 'left',
      width: 80,
      render: (col) => {
        let method = col;
        let attributionIcon = cnnaip;
        let tip = '';
        //Single object with 1 key
        if (method) {
          if (method === 'COOKIE') {
            attributionIcon = cnnaid;
            tip = 'Cookie Match - Deterministic match using browser based cookie / uuid match.';
          } else if (method === 'FINGERPRINT') {
            attributionIcon = cnnafingerprint;
            tip =
              'User Match - Probabilistic match using user fingerprint. (User fingerprint - The combination of user agent and IP address to produce a unique hash)';
          } else if (method === 'DEVICE_FINGERPRINT') {
            tip =
              'Device Match - Probabilistic match using user device model information (Device model, OS, OS version ) combined with IP address';
          } else if (method === 'IP') {
            tip = 'IP Match - Probabilistic match using IP address.';
            attributionIcon = cnnaip;
          }
        }
        const attributionIcn = () => {
          if (method === 'DEVICE_FINGERPRINT') {
            return (
              <span role="img" className="anticon" style={{ verticalAlign: '-5px' }}>
                <GlobePhoneIcon
                  height="35"
                  width="35"
                  style={{
                    opacity: '1.0',
                  }}
                />
              </span>
            );
          } else {
            return <Avatar src={attributionIcon} />;
          }
        };
        return <Tooltip title={tip}>{attributionIcn()}</Tooltip>;
      },
    },
  ];
  const handleExpand = (expanded, record) => {
    if (expanded && !expandedRowsData[record.key]) {
      if (setExpandedRowsData) {
        setExpandedRowsData((prev) => ({
          ...prev,
          [record.key]: { loading: true, impressions: [], sessionJourney: [] },
        }));
      }
      if (fetchSessionDetails) {
        fetchSessionDetails({
          variables: {
            where: { key: record.key },
          },
        });
      }
    }
  };

  return (
    <React.Fragment>
      <Card
        title={
          <span>
            Sessions{' '}
            <Tooltip title={SESSIONS_TOOLTIP}>
              <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
            </Tooltip>
          </span>
        }
      >
        <Table
          dataSource={data}
          columns={columns}
          loading={loading}
          onChange={(paginationObject) => {
            const nextPage = location.pathname.split('/');
            nextPage[4] = paginationObject.current;
            const searchParams = new URLSearchParams(window.location.search);
            if (searchParams.get('pageSize')) searchParams.delete('pageSize');
            if (!isEmpty(paginationObject) && paginationObject.pageSize > 10) {
              searchParams.append('pageSize', paginationObject.pageSize);
            }
            history.push({
              pathname: nextPage.join('/'),
              search: `?${searchParams.toString()}`,
            });
          }}
          pagination={{
            position: ['none', 'bottomCenter'],
            total: total ? total : data.length,
            responsive: true,
            showSizeChanger: false,
            showLessItems: true,
            size: 'default',
            current: Number(current),
          }}
          rowKey={({ key }) => key}
          scroll={{ x: 1200, y: 1700 }}
          expandable={{
            expandedRowRender: (record) => {
              const rowData = expandedRowsData[record.key] || {
                loading: true,
                impressions: [],
                sessionJourney: [],
              };
              return (
                <SessionJourneyExpand
                  impressions={rowData.impressions}
                  journey={rowData.sessionJourney}
                  loading={rowData.loading}
                />
              );
            },
            onExpand: handleExpand,
          }}
        />
      </Card>
    </React.Fragment>
  );
};

export default withRouter(SessionsTable);
